import React, { useState, useEffect } from 'react';
import { Eye, Search, Scale, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { HistoriqueService } from '../services/HistoriqueService';
import { nextPage, previousPage } from '../utils/pagination';

const HistoriqueJustice = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedUser, setSelectedUser] = useState('all');
    const [selectedPeriod, setSelectedPeriod] = useState('all');
    const [searches, setSearches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({
        page: 1,
        pageSize: 20,
        total: 0,
        next: null,
        previous: null
    });

    // Charger les données depuis l'API (chargement initial et recherche)
    useEffect(() => {
        const controller = new AbortController();

        const fetchData = async () => {
            setLoading(true);
            setError(null);

            try {
                const filters = {
                    type: 'decision_justice,chatbot',
                    page: 1,
                    page_size: pagination.pageSize,
                };

                // Ajouter la recherche si elle existe
                if (searchTerm) {
                    filters.request = searchTerm;
                }

                const response = await HistoriqueService.getHistory(filters, controller.signal);

                setSearches(response.results || []);
                setPagination(prev => ({
                    ...prev,
                    page: 1,
                    total: response.count || 0,
                    next: response.next,
                    previous: response.previous
                }));
            } catch (err) {
                if (err.name !== 'AbortError') {
                    setError('Erreur lors du chargement des données');
                    console.error('Error fetching justice history:', err);
                }
            } finally {
                setLoading(false);
            }
        };

        fetchData();

        return () => controller.abort();
    }, [searchTerm]);

    // Gestion de la navigation - Page suivante
    const handleNextPage = async () => {
        if (!pagination.next) return;

        setLoading(true);
        setError(null);

        try {
            const response = await nextPage(pagination.next);

            setSearches(response.results || []);
            setPagination(prev => ({
                ...prev,
                page: prev.page + 1,
                total: response.count || 0,
                next: response.next,
                previous: response.previous
            }));
        } catch (err) {
            setError('Erreur lors du chargement de la page suivante');
            console.error('Error fetching next page:', err);
        } finally {
            setLoading(false);
        }
    };

    // Gestion de la navigation - Page précédente
    const handlePreviousPage = async () => {
        if (!pagination.previous) return;

        setLoading(true);
        setError(null);

        try {
            const response = await previousPage(pagination.previous);

            setSearches(response.results || []);
            setPagination(prev => ({
                ...prev,
                page: prev.page - 1,
                total: response.count || 0,
                next: response.next,
                previous: response.previous
            }));
        } catch (err) {
            setError('Erreur lors du chargement de la page précédente');
            console.error('Error fetching previous page:', err);
        } finally {
            setLoading(false);
        }
    };

    // Filtrer les recherches localement pour l'utilisateur
    const filteredSearches = searches.filter(search => {
        const userEmail = search.user?.email || null;
        const matchesUser = selectedUser === 'all' || userEmail === selectedUser;
        return matchesUser;
    });

    // Extraire les utilisateurs uniques
    const uniqueUsers = Array.from(new Set(searches.map(s => s.user?.email).filter(u => u)));

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Calculer les informations de pagination
    const totalPages = Math.ceil(pagination.total / pagination.pageSize);
    const startItem = (pagination.page - 1) * pagination.pageSize + 1;
    const endItem = Math.min(pagination.page * pagination.pageSize, pagination.total);

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="bg-orange-500 p-3 rounded-lg">
                            <Scale className="text-white" size={32} />
                        </div>
                        <h1 className="text-3xl font-bold text-orange-600">Recherches Justice</h1>
                    </div>
                    <p className="text-gray-600">Analyse des recherches dans les juridictions et décisions de justice</p>
                </div>

                {/* Filters Section */}
                <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
                    <div className="flex items-center gap-2 mb-4">
                        <Search className="text-orange-500" size={20} />
                        <h2 className="text-lg font-semibold text-gray-800">Filtres et Recherche</h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Recherche dans toutes les colonnes
                            </label>
                            <input
                                type="text"
                                placeholder="Rechercher dans toutes les colonnes..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full px-4 py-3 text-base text-gray-900 placeholder-gray-400 bg-white border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Filtrer par utilisateur
                            </label>
                            <select
                                value={selectedUser}
                                onChange={(e) => setSelectedUser(e.target.value)}
                                className="w-full px-4 py-3 text-base text-gray-900 bg-white border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors appearance-none cursor-pointer"
                            >
                                <option value="all">Tous les utilisateurs</option>
                                {uniqueUsers.map(user => (
                                    <option key={user} value={user}>{user || 'Anonyme'}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Filtrer par période
                            </label>
                            <select
                                value={selectedPeriod}
                                onChange={(e) => setSelectedPeriod(e.target.value)}
                                className="w-full px-4 py-3 text-base text-gray-900 bg-white border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors appearance-none cursor-pointer"
                            >
                                <option value="all">Toutes les dates</option>
                                <option value="today">Aujourd'hui</option>
                                <option value="week">Cette semaine</option>
                                <option value="month">Ce mois</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Results Section */}
                <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                    <div className="bg-orange-50 p-4 border-b border-orange-200">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <Scale className="text-orange-600" size={24} />
                                <div>
                                    <h2 className="text-xl font-bold text-gray-900">Historique des Recherches Justice</h2>
                                    <p className="text-sm text-gray-600">{filteredSearches.length} résultats trouvés</p>
                                </div>
                            </div>

                            {/* Pagination info */}
                            {pagination.total > 0 && (
                                <div className="text-sm text-gray-600">
                                    Affichage de {startItem} à {endItem} sur {pagination.total} résultats
                                </div>
                            )}
                        </div>
                    </div>

                    {error && (
                        <div className="p-4 bg-red-50 border-l-4 border-red-500 text-red-700">
                            {error}
                        </div>
                    )}

                    {loading ? (
                        <div className="flex items-center justify-center p-12">
                            <Loader2 className="animate-spin text-orange-500" size={48} />
                        </div>
                    ) : (
                        <>
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-gray-50 border-b border-gray-200">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Date & Heure</th>
                                            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Utilisateur</th>
                                            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Mot-clé</th>
                                            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Source</th>
                                            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Address IP</th>
                                            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Nb Credit</th>
                                            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {filteredSearches.map((search) => (
                                            <tr key={search.id} className="hover:bg-gray-50 transition-colors">
                                                <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                                                    {formatDate(search.created_at)}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${!search.user?.email
                                                        ? 'bg-gray-100 text-gray-700'
                                                        : 'bg-orange-50 text-orange-700 border border-orange-200'
                                                        }`}>
                                                        {search.user?.email || search.company?.name || 'Anonyme'}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-sm font-medium text-gray-900">
                                                    {search.request || '-'}
                                                </td>
                                                <td className="px-6 py-4 text-sm font-medium text-gray-900">
                                                    {search.type || '-'}
                                                </td> <td className="px-6 py-4 text-sm font-medium text-gray-900">
                                                    {search.ip_address || '-'}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className="inline-flex items-center justify-center px-3 py-1 rounded-full text-sm font-semibold bg-green-100 text-green-700">
                                                        {search.credit_used || 0} crédits
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <button className="inline-flex items-center gap-2 px-5 py-2.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-medium shadow-sm">
                                                        <Eye size={18} />
                                                        Consulter les détails
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {filteredSearches.length === 0 && !loading && (
                                <div className="p-12 text-center">
                                    <Scale className="mx-auto text-gray-300 mb-4" size={64} />
                                    <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun résultat trouvé</h3>
                                    <p className="text-gray-600">Essayez de modifier vos critères de recherche</p>
                                </div>
                            )}

                            {/* Pagination Controls */}
                            {pagination.total > 0 && (
                                <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                                    <div className="flex items-center justify-between">
                                        <div className="text-sm text-gray-700">
                                            Page <span className="font-medium">{pagination.page}</span> sur{' '}
                                            <span className="font-medium">{totalPages}</span>
                                        </div>

                                        <div className="flex gap-2">
                                            <button
                                                onClick={handlePreviousPage}
                                                disabled={!pagination.previous}
                                                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${pagination.previous
                                                    ? 'bg-orange-500 text-white hover:bg-orange-600'
                                                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                                    }`}
                                            >
                                                <ChevronLeft size={18} />
                                                Précédent
                                            </button>

                                            <button
                                                onClick={handleNextPage}
                                                disabled={!pagination.next}
                                                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${pagination.next
                                                    ? 'bg-orange-500 text-white hover:bg-orange-600'
                                                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                                    }`}
                                            >
                                                Suivant
                                                <ChevronRight size={18} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default HistoriqueJustice;